import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  Legend,
  CartesianGrid,
} from "recharts";
import ReportHeader from "../../../../../components/Reports/ReportHeader";
import PrintButton from "../../../../../components/Reports/Buttons/PrintButton";
import { ChartStyles } from "../../Styles/QuarterlyStyles";
import { useGetIsOperatingSystemWindows } from "utils/customHooks/useGetIsOperatingSystemWindows";
import { ReportPageProps } from "../../../../../types";
import { QiagenQuarterlyDataType } from "../../utils/QiagenQuarterlyUtils";
export const tickGenerator = tick => {
  const { x, y, payload } = tick;
  const { offset, value }: { offset: number; value: string } = payload;

  const tickDetails = value.split(":");

  const machineName = tickDetails[0];
  const isMiddle = tickDetails.find(tickValue => tickValue === "middle");
  const isStart = tickDetails.find(tickValue => tickValue === "start");
  const isEnd = tickDetails.find(tickValue => tickValue === "end");
  const count = tickDetails[tickDetails.length - 1];
  const isEven = +count % 2 === 0; // + converts count to a number
  const centeringOffset = isEven ? offset : 0; // if even we need to center between two ticks, otherwise the tick itself is centered

  const pathXTop = Math.floor(x) + offset; // to position the top line a half tick up
  const pathXBot = Math.floor(x) - offset; // same for bottom line

  const middleText = isMiddle ? (
    <text
      x={x - 100}
      y={y + centeringOffset}
      textAnchor="middle"
      stroke="#666"
      fontSize="12"
    >
      {machineName}
    </text>
  ) : null;

  const topLine = isStart ? (
    <path
      d={`M${pathXTop},${y + 70}v${-500}`}
      stroke="#666"
      transform={`rotate(-90, ${x}, ${y})`}
    />
  ) : null;
  const bottomLine = isEnd ? (
    <path
      d={`M${pathXBot},${y + 70}v${-500}`}
      stroke="#666"
      transform={`rotate(-90, ${x}, ${y})`}
    />
  ) : null;
  return (
    <>
      {topLine} {middleText} {bottomLine}
    </>
  );
};
/**
 * Render a custom legend with existing bars, and adding Percent Yield
 *
 * @param {*} props
 * @returns
 */
const renderLegend = props => {
  const { payload } = props;
  const svgRect = (color: string) => {
    return (
      <svg width="12" height="12" style={{ marginRight: "5px" }}>
        <rect width="12" height="12" style={{ fill: color }} />
      </svg>
    );
  };
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {payload.map((entry, index) => (
        <div
          style={{
            paddingRight: "10px",
            display: "flex",
            alignItems: "center",
          }}
          key={`item-${index}`}
        >
          {svgRect(entry.color)}
          {entry.value}
        </div>
      ))}
      <div style={{ display: "flex", alignItems: "center" }}>
        {svgRect("black")}
        Percent Yield
      </div>
    </div>
  );
};
/**
 * generate and create bar chart based on given data
 */

export default function QuarterlyReportBarChart({
  subHeader,
  headerTitle,
  dateString,
  title,
  width,
  data,
}: ReportPageProps<QiagenQuarterlyDataType[]>) {
  const isWindows = useGetIsOperatingSystemWindows();
  return (
    <ChartStyles>
      <ReportHeader
        title={headerTitle}
        dateString={dateString}
        rightSideComponent={<PrintButton />}
        subheader={subHeader}
      />
      <h1 className="chartTitle" key={title + ": actualTitle"}>
        {title}
      </h1>
      <BarChart
        layout="vertical"
        width={width}
        height={isWindows ? 1017 : 1013}
        data={data}
        margin={{
          top: 20,
          right: 20,
          left: 140,
          bottom: 30,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis
          type="category"
          dataKey="skuNameStatic"
          yAxisId={0} // ID is important or the second axis does not render
          tick={{ fontSize: 12 }}
        />
        <YAxis
          type="category"
          yAxisId={1}
          dataKey="machineName"
          axisLine={false}
          tickLine={false}
          tick={tickGenerator}
        />
        <Legend content={renderLegend} height={2} />
        <Bar
          name="Total Production Achieved"
          dataKey="totalUnitsProduced"
          barSize={12}
          fill="#182E58"
          layout="vertical"
        >
          <LabelList dataKey="percentYield" position="right" />
        </Bar>
        <Bar
          name="Expected Target Production"
          dataKey="expectedTargetProduced"
          barSize={12}
          fill="#0BA6D6"
          layout="vertical"
        />
      </BarChart>
    </ChartStyles>
  );
}
